import {
  Button,
  CheckboxField,
  Flex,
  Grid,
  Label,
  Radio,
  RadioGroupField,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import useErrors from "hooks/useErrors";
import { CTooltip } from "@coreui/react";
import "./style.css";

const VariableForm = ({ onSubmit, type = "create", initialValues }) => {
  const [variableForm, setVariableForm] = useState(initialValues);
  const [errors, setErrors] = useState({
    name: "",
    shortName: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    shortName: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(variableForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (!hasErrors) onSubmit(variableForm);
  };

  const onChange = (e) => {
    const { name, value, checked } = e.target;
    setVariableForm((prevSt) => ({
      ...prevSt,
      [name]: name === "derived" ? checked : value,
    }));
    checkErrors(name, value);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      shortName: "",
    });
    setVariableForm(initialValues);
  };

  useEffect(() => {
    setVariableForm(initialValues);
  }, [initialValues]);

  return (
    <Grid rowGap="15px" columnGap="15px" padding="20px">
      <Grid
        columnGap="15px"
        templateColumns={{ base: "1fr", large: "2fr 2fr" }}
      >
        <TextField
          name="name"
          label={
            <span>
              {strings.fields.name.label}
              <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
            </span>
          }
          value={variableForm.name}
          onChange={onChange}
          errorMessage={errors.name}
          hasError={!!errors.name}
        ></TextField>
        <TextField
          name="shortName"
          label={
            <span>
              {strings.fields.shortName.label}
              <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
            </span>
          }
          value={variableForm.shortName}
          onChange={onChange}
          errorMessage={errors.shortName}
          hasError={!!errors.shortName}
        ></TextField>
      </Grid>
      <TextAreaField
        name="description"
        label={strings.fields.description.label}
        value={variableForm.description}
        onChange={onChange}
      ></TextAreaField>
      <Grid
        columnGap="15px"
        rowGap="15px"
        templateColumns={{ base: "1fr", large: "1fr 1fr 2fr" }}
      >
        <SelectField
          name="type"
          value={variableForm.type}
          label={strings.fields.type.label}
          onChange={onChange}
        >
          {Object.entries(strings.fields.type.options).map((opt) => (
            <option value={opt[0]}>{opt[1]}</option>
          ))}
        </SelectField>
        <TextField
          name="unit"
          label={strings.fields.unit.label}
          value={variableForm.unit}
          onChange={onChange}
          errorMessage={errors.unit}
          hasError={!!errors.unit}
        ></TextField>
        <Grid>
          <Label>
            {strings.fields.groupType.label}&nbsp;
            <CTooltip content={strings.fields.groupType.help}>
              <HelpOutlineIcon className="mb-1" fontSize="small" />
            </CTooltip>
          </Label>
          <RadioGroupField
            name="groupType"
            value={variableForm.groupType ?? "1"}
            onChange={onChange}
            direction="row"
          >
            {Object.entries(strings.fields.groupType.options).map((opt) => (
              <Radio value={opt[0]}>{opt[1]}</Radio>
            ))}
          </RadioGroupField>
        </Grid>
        <CheckboxField
          name="derived"
          label={strings.fields.derived.label}
          checked={variableForm.derived}
          onChange={onChange}
        ></CheckboxField>
      </Grid>
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      shortName: {
        label: "Nombre corto",
      },
      unit: {
        label: "Unidad",
      },
      type: {
        label: "Tipo",
        options: {
          numeric: "Numérica",
          onOff: "OnOff",
        },
      },
      derived: {
        label: "Derivado",
      },
      groupType: {
        label: "Tipo de agrupación",
        help: "Este parámetro permite seleccionar el cálculo agregado en el momento de establecer condiciones para el modulo de Reglas y Alertas. Si esta variable utiliza el cálculo agregado de suma/acumulado debe seleccionar “Requiere periodo de tiempo”, o si la variable permite seleccionar los cálculos agregados de promedio, minimo y maximo, debe seleccionar “Es instantánea”.",
        options: {
          1: "Requiere periodo de tiempo",
          2: "Es instantánea",
        },
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
  },
});

export default VariableForm;
